import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const LoginPage = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!email || !password) {
            toast.error('Please fill in all fields');
            return;
        }

        setLoading(true);
        try {
            const user = await login(email, password);
            toast.success('Login successful!');
            
            // Redirect based on role
            if (user.role === 'admin') {
                navigate('/admin');
            } else if (user.role === 'barber') {
                navigate('/team');
            } else {
                navigate('/');
            }
        } catch (error) {
            console.error('Login failed:', error);
            toast.error(error.response?.data?.detail || 'Invalid email or password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-dark">
            <Navbar />

            <div className="pt-32 pb-12 px-6 md:px-12 min-h-screen flex items-center justify-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-md"
                >
                    {/* Logo */}
                    <div className="text-center mb-8">
                        <img
                            src="https://customer-assets.emergentagent.com/job_barber-booking-241/artifacts/03303zlc_96f21524-c6ba-4411-86fb-3aa42208d697.jpeg"
                            alt="RL Barbershop"
                            className="h-20 w-20 mx-auto mb-4"
                        />
                        <h1 className="font-anton text-3xl">STAFF LOGIN</h1>
                        <p className="text-zinc-500 font-urbanist mt-2">
                            Access your dashboard
                        </p>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                        <div>
                            <label className="block text-zinc-400 font-urbanist text-sm mb-2">
                                Email
                            </label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@rlbarbershop.com"
                                data-testid="login-email"
                                className="bg-transparent border-zinc-700 focus:border-primary text-white placeholder:text-zinc-600"
                            />
                        </div>

                        <div>
                            <label className="block text-zinc-400 font-urbanist text-sm mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    data-testid="login-password"
                                    className="bg-transparent border-zinc-700 focus:border-primary text-white placeholder:text-zinc-600 pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-btn"
                            className="w-full bg-primary text-black font-anton tracking-wider py-6 hover:bg-white neon-glow"
                        >
                            {loading ? 'Logging in...' : 'LOGIN'}
                            <LogIn className="ml-2" size={18} />
                        </Button>
                    </form>

                    {/* Demo Credentials */}
                    <div className="mt-8 p-4 bg-dark-paper border border-zinc-800">
                        <p className="text-zinc-500 font-urbanist text-sm mb-2">Demo Admin Credentials:</p>
                    </div>

                    {/* Back Link */}
                    <div className="text-center mt-6">
                        <Link to="/" className="text-zinc-500 hover:text-primary font-urbanist text-sm">
                            ← Back to Home
                        </Link>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default LoginPage;
